import React from 'react'
import { motion } from 'framer-motion'
import { Mail, MapPin, Github, Linkedin, Globe } from 'lucide-react'
import { Profile } from '../types'
import { fadeUp, staggerContainer, viewportOnce, wiggle } from '../lib/motion'

interface ContactProps {
  profile: Profile
}

const Contact: React.FC<ContactProps> = ({ profile }) => {

  const contactMethods = [
    {
      icon: <Mail className="h-6 w-6" />,
      label: 'Email',
      value: profile.email,
      href: `mailto:${profile.email}`,
      color: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
    },
    {
      icon: <MapPin className="h-6 w-6" />,
      label: 'Location',
      value: profile.location,
      href: `https://maps.google.com/?q=${encodeURIComponent(profile.location)}`,
      color: 'bg-accent-100 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400'
    }
  ]

  const socialLinks = [
    {
      icon: <Github className="h-5 w-5" />,
      label: 'GitHub',
      url: profile.social?.find(s => s.name === 'GitHub')?.url || '',
      color: 'hover:bg-gray-100 dark:hover:bg-gray-800'
    },
    {
      icon: <Linkedin className="h-5 w-5" />,
      label: 'LinkedIn',
      url: profile.social?.find(s => s.name === 'LinkedIn')?.url || '',
      color: 'hover:bg-accent-100 dark:hover:bg-accent-900/30'
    },
    {
      icon: <Globe className="h-5 w-5" />,
      label: 'Website',
      url: profile.social?.find(s => s.name === 'Website')?.url || '',
      color: 'hover:bg-accent-100 dark:hover:bg-accent-900/30'
    }
  ]

  return (
    <section id="contact" className="py-20 bg-white dark:bg-gray-900">
      <div className="container-max section-padding">
        <motion.div
          className="text-center mb-8 sm:mb-12 lg:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
            Get In Touch
          </h2>
          <div className="w-16 sm:w-20 md:w-24 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full"></div>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-3 sm:mt-4 max-w-2xl mx-auto px-4">
            I'm always open to discussing new opportunities, interesting projects, or just having a chat about technology.
          </p>
        </motion.div>

        <motion.div
          className="grid lg:grid-cols-1 gap-8 sm:gap-12 lg:gap-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          {/* Contact Information */}
          <motion.div variants={fadeUp}>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8">
              Let's Connect
            </h3>

            <motion.div
              className="space-y-4 sm:space-y-6 mb-6 sm:mb-8"
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              variants={staggerContainer}
            >
              {contactMethods.map((method, index) => (
                <motion.a
                  key={index}
                  href={method.href}
                  target={method.label === 'Location' ? '_blank' : '_self'}
                  rel={method.label === 'Location' ? 'noopener noreferrer' : undefined}
                  className="flex items-center p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-300 group"
                  variants={fadeUp}
                  whileHover={{ x: 4 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  <div className={`p-2 sm:p-3 rounded-lg mr-3 sm:mr-4 ${method.color} group-hover:scale-110 transition-transform duration-300`}>
                    {method.icon}
                  </div>
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                      {method.label}
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      {method.value}
                    </p>
                  </div>
                </motion.a>
              ))}
            </motion.div>

            {/* Social Links */}
            <div>
              <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                Follow Me
              </h4>
              <div className="flex space-x-3 sm:space-x-4">
                {socialLinks.map((social, index) => (
                  social.url && (
                    <motion.a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`p-2 sm:p-3 rounded-lg border border-gray-200 dark:border-gray-700 ${social.color} transition-colors duration-300`}
                      title={social.label}
                      whileHover={{ scale: 1.12, ...wiggle.whileHover }}
                      whileTap={{ scale: 0.95 }}
                      transition={wiggle.transition}
                    >
                      {social.icon}
                    </motion.a>
                  )
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-xl">
              <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                Quick Response
              </h4>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-bold text-primary-600 dark:text-primary-400">
                    &lt; 24h
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    Email Response
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-bold text-primary-600 dark:text-primary-400">
                    100%
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    Response Rate
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default Contact
